import { toast } from "react-toastify";
import { isAxiosError } from "axios";

import {
    getToken,
    IGetTokenDataRequest,
    IGetTokenWithGoogleTokenRequest,
    IGetTokenResponse,
    refreshToken,
    getTokenWithGoogle as getTokenWithGoogleIntegration,
} from "./integrations/auth";
import { buildLoginRedirectUrl, isPublicAuthRoute } from "../commons/helpers";
import { useUtils } from "../contexts/UtilsContext";
import TokenManager from "../commons/TokenManager";

interface IUseAuth {
    getAccessToken: (data: IGetTokenDataRequest) => Promise<IGetTokenResponse | undefined>;
    getTokenWithGoogle: (
        data: IGetTokenWithGoogleTokenRequest
    ) => Promise<IGetTokenResponse | undefined>;
    refreshAccessToken: () => Promise<IGetTokenResponse | undefined>;
}

function getApiErrorDetail(error: unknown) {
    if (!isAxiosError(error)) {
        return;
    }

    const detail = error.response?.data?.detail;

    if (typeof detail === "string") {
        return detail;
    }

    if (detail && typeof detail === "object") {
        const firstMessage = Object.values(detail)[0];

        if (typeof firstMessage === "string") {
            return firstMessage;
        }
    }
}

export default function useAuth(): IUseAuth {
    const { setIsLoading } = useUtils();

    const getAccessToken = async (data: IGetTokenDataRequest) => {
        try {
            setIsLoading(true);
            const response = await getToken(data);
            return response.data;
        } catch (error: unknown) {
            if (isAxiosError(error)) {
                if (error.response?.status === 401 || error.response?.status === 423) {
                    toast.error(getApiErrorDetail(error) || "Credenciais invalidas.");
                    return;
                }
            }
            toast.error(getApiErrorDetail(error) || "Tivemos um problema ao fazer login.");
        } finally {
            setIsLoading(false);
        }
    };

    const getTokenWithGoogle = async (data: IGetTokenWithGoogleTokenRequest) => {
        try {
            setIsLoading(true);
            const response = await getTokenWithGoogleIntegration(data);
            return response.data;
        } catch (error: unknown) {
            if (isAxiosError(error)) {
                if (error.response?.status === 401 || error.response?.status === 423) {
                    toast.error(getApiErrorDetail(error) || "Credenciais invalidas.");
                    return;
                }
            }
            toast.error(getApiErrorDetail(error) || "Tivemos um problema ao fazer login.");
        } finally {
            setIsLoading(false);
        }
    };

    const refreshAccessToken = async () => {
        const tokenRefresh = TokenManager.getRefreshToken();
        if (!tokenRefresh) {
            return;
        }
        try {
            setIsLoading(true);
            const response = await refreshToken({
                refreshToken: tokenRefresh,
            });
            return response.data;
        } catch (error: unknown) {
            if (isAxiosError(error)) {
                if (error.response?.status === 401) {
                    toast.error("Sessao expirada, faca login novamente.");
                    TokenManager.clear();
                    const isPublicRoute = isPublicAuthRoute(window.location.pathname);
                    if (!isPublicRoute) {
                        window.location.href = buildLoginRedirectUrl();
                    }
                    return;
                }
            }
            toast.error(getApiErrorDetail(error) || "Tivemos um problema, faca login novamente.");
        } finally {
            setIsLoading(false);
        }
    };

    return { getAccessToken, refreshAccessToken, getTokenWithGoogle };
}
